/**
 * Utility functions for category detection settings
 */

// Client-side settings cache for efficient access
let categoryDetectionCache: {
  admin_enabled: boolean;
  seller_enabled: boolean;
  expiry: number;
} | null = null;

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get category detection settings from client-side cache or fetch if needed
 * @returns Object with admin and seller detection enabled status
 */
export async function getCategoryDetectionSettings(): Promise<{
  admin_enabled: boolean;
  seller_enabled: boolean;
}> {
  const now = Date.now();

  // Return cached settings if still valid
  if (categoryDetectionCache && now < categoryDetectionCache.expiry) {
    return {
      admin_enabled: categoryDetectionCache.admin_enabled,
      seller_enabled: categoryDetectionCache.seller_enabled,
    };
  }

  try {
    const response = await fetch("/api/settings/system", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.warn(
        "[CATEGORY_DETECTION] Failed to fetch settings, defaulting to safe values",
      );
      // Default to disabled for seller (user), enabled for admin
      return {
        admin_enabled: true,
        seller_enabled: false,
      };
    }

    const data = await response.json();
    const settings = data.settings || {};

    const adminEnabled = settings.admin_category_detection_enabled ?? true;
    const sellerEnabled = settings.seller_category_detection_enabled ?? false;

    // Cache the result
    categoryDetectionCache = {
      admin_enabled: adminEnabled,
      seller_enabled: sellerEnabled,
      expiry: now + CACHE_DURATION,
    };

    return {
      admin_enabled: adminEnabled,
      seller_enabled: sellerEnabled,
    };
  } catch (error) {
    console.warn("[CATEGORY_DETECTION] Error fetching settings:", error);
    // Default to disabled for seller (user), enabled for admin
    return {
      admin_enabled: true,
      seller_enabled: false,
    };
  }
}

/**
 * Clear the category detection settings cache
 * Useful when settings are updated
 */
export function clearCategoryDetectionCache(): void {
  categoryDetectionCache = null;
}
